import React, { FC, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

import leftArrow from 'assets/images/gray-nav-arrow-left.svg';
import rightArrow from 'assets/images/gray-nav-arrow-right.svg';

import color from 'atoms/colors/colors';

import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

import type { ContentfulInPageFeatures } from 'graphqlTypes';

const InPageFeaturesMobile: FC<ContentfulInPageFeatures> = ({ inPageNavigationItems }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);

  return (
    <Container className="w-full pb-12" id="features-mobile">
      <div className="max-w-[575px] ml-auto mr-auto my-0 mb-7 relative h-7">
        <Slider
          asNavFor={nav2}
          ref={slider => setNav1(slider)}
          slidesToShow={1}
          swipeToSlide
          focusOnSelect
          centerMode
          arrows={false}
          variableWidth
        >
          {inPageNavigationItems?.map((item, index) => (
            <React.Fragment key={`nav-${index}`}>
              <div className="flex items-center justify-center px-4 py-0 cursor-pointer" key={index}>
                <Text
                  as="p"
                  textStyle="textSm"
                  textColor="gray-900"
                  fontWeight="medium"
                  className={`title ${(!activeIndex && index === 0) || activeIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  {item?.title}
                </Text>
              </div>
            </React.Fragment>
          ))}
        </Slider>
      </div>
      <div>
        <Slider asNavFor={nav1} ref={slider => setNav2(slider)} arrows dots>
          {inPageNavigationItems?.map((item, index) => {
            const featuredImage =
              item?.featuredImage?.bynderImageDesktop?.[0]?.thumbnails?.src ||
              item?.featuredImage?.imageDesktop?.file?.url;

            return (
              <React.Fragment key={`in-page-nav-main-${index}`}>
                <div
                  className="h-[275px] bg-contain bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${featuredImage})` }}
                />
                <div key={index}>
                  {item?.description && (
                    <div className="description mx-0 mt-8 mb-5 text-center">{richTextParser(item.description)}</div>
                  )}
                  {item?.link && <ComponentButton label="Learn More" link={item.link} />}
                </div>
              </React.Fragment>
            );
          })}
        </Slider>
      </div>
    </Container>
  );
};

export default InPageFeaturesMobile;

export const Container = styled.div`
  .titles {
    &:before,
    &:after {
      position: absolute;
      content: ' ';
      width: 50px;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }

    &:before {
      left: -2px;
      background: linear-gradient(to right, rgb(255, 255, 255), rgba(255, 255, 255, 0));
    }

    &:after {
      right: -1px;
      top: 0;
      background: linear-gradient(to left, rgb(255, 255, 255), rgba(255, 255, 255, 0));
    }
  }

  .slick-current p {
    color: ${color.green[300]};
  }

  .description {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-size: 20px;
      margin-bottom: 10px;
      color: ${color.gray[900]};
    }

    p {
      font-size: 18px;
      text-align: center;
      color: ${color.gray[900]};
    }

    @media (max-width: 991px) {
      p {
        font-size: 16px;
      }
    }
  }

  .MuiButton-root {
    margin: 0 auto 30px auto;
  }

  .slick-prev,
  .slick-next {
    bottom: -52px;
    border-radius: 50%;
    top: auto;
    width: 32px;
    height: 32px;
    z-index: 1;
    box-shadow: 0 3px 6px 1px rgba(160, 161, 188, 0.35);

    &:before {
      width: 32px;
      height: 32px;
      color: transparent;
    }
  }

  .slick-prev {
    left: 0;
    background-image: url('${leftArrow}') !important;
    background-color: var(--white) !important;
    background-repeat: no-repeat !important;
    background-size: 9px !important;
    background-position: center !important;
  }

  .slick-next {
    right: 0;
    background-image: url('${rightArrow}') !important;
    background-color: var(--white) !important;
    background-repeat: no-repeat !important;
    background-size: 9px !important;
    background-position-x: 13px !important;
    background-position-y: center !important;
  }

  .slick-dots {
    bottom: -34px;

    li {
      margin: 0 1px;

      button:before {
        font-size: 14px;
        opacity: 1;
        color: ${color.gray[200]};
      }

      &.slick-active {
        button:before {
          color: ${color.green[300]};
        }
      }
    }
  }
`;
